// Mutual exclusion for the two things in this cache that can genuinely race:
// draining the sale queue, and taking the next receipt number out of the reserved
// block.
//
// TWO LAYERS, because one is not enough:
//
//   in-process — a promise chain per lock name. Web Locks only arbitrates BETWEEN
//     contexts; two `await`s in the same tab interleave freely, so without this a
//     single tab firing two takes at once still read-modify-writes the same block
//     twice and hands the same number to two customers.
//
//   Web Locks — arbitrates between tabs of the same origin, which share one
//     IndexedDB but not one heap.
//
// Callers must still be correct without either, because Web Locks can be absent
// (very old browsers, and node under vitest). Here that means the server's own
// guards: checkout is idempotent on the sale id, and a receipt number already on
// the books is re-minted and the sale flagged rather than rejected.

export type LockMode = 'wait' | 'skip'

/** Tail of the in-process queue per lock name. */
const chains = new Map<string, Promise<unknown>>()
/** Lock names currently held in THIS context, for 'skip'. */
const held = new Set<string>()

async function viaWebLock<T>(
  name: string,
  mode: LockMode,
  fn: () => Promise<T>
): Promise<T | undefined> {
  if (typeof navigator === 'undefined' || !navigator.locks) return fn()
  // Type argument left to inference on purpose. lib.dom types the callback as
  // returning T exactly, not T | PromiseLike<T>, so naming T here fights the
  // signature; inferred, T becomes the promise itself and this async function
  // flattens it on the way out, which is what the runtime does anyway.
  if (mode === 'wait') return navigator.locks.request(name, () => fn())
  return navigator.locks.request(name, { ifAvailable: true }, (lock) =>
    lock ? fn() : Promise.resolve(undefined)
  )
}

/**
 * Run `fn` while holding `name`.
 *
 * 'wait' queues behind whoever holds it — the right mode for taking a receipt
 * number, which must produce an answer.
 *
 * 'skip' gives up immediately when the lock is held, and `fn` never runs, so the
 * result is undefined — the right mode for draining the queue, where another tab
 * already doing it means there is nothing to do and waiting in line would just
 * pile timers up behind it. Callers must be able to tell that undefined from a
 * real result.
 */
export async function withLock<T>(
  name: string,
  mode: LockMode,
  fn: () => Promise<T>
): Promise<T | undefined> {
  if (mode === 'skip') {
    if (held.has(name)) return undefined
    held.add(name)
    try {
      return await viaWebLock(name, mode, fn)
    } finally {
      held.delete(name)
    }
  }

  // Chain onto whatever is already queued for this name, whether it succeeded or
  // not — a previous holder's failure must not wedge every later caller.
  const prev = chains.get(name) ?? Promise.resolve()
  const run = prev.then(
    () => viaWebLock(name, mode, fn),
    () => viaWebLock(name, mode, fn)
  )
  // The stored tail never rejects, so it is safe to await unguarded above.
  chains.set(
    name,
    run.catch(() => undefined)
  )
  return run
}

/** Test seam. */
export function _resetLocks(): void {
  chains.clear()
  held.clear()
}
